import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Clock, Coffee, PlayCircle, PauseCircle, LogOut, ChevronDown } from "lucide-react";
import useAccessiblePopover from "@/hooks/useAccessiblePopover";

/** Small clock-in/out widget that lives in the dashboard topbar.
 *
 * Shows the current user's shift state with a single primary button:
 *   - "Clock in"   (when offline)
 *   - "On the clock · 02:14"  + dropdown for status / clock-out
 */
export default function ClockWidget() {
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState(false);
  const { panelRef, triggerRef, closeAndRestore } = useAccessiblePopover(menu, () => setMenu(false));

  const load = async () => {
    try {
      const { data } = await api.get("/staff/me");
      setMe(data);
    } catch (_) { /* tolerate */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  const clockIn = async () => {
    setBusy(true);
    try {
      await api.post("/staff/clock-in", { device: "web" });
      toast.success("Clocked in");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const clockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/clock-out", {});
      toast.success(
        data.duration_minutes != null
          ? `Clocked out · ${Math.floor(data.duration_minutes / 60)}h ${data.duration_minutes % 60}m`
          : "Clocked out"
      );
      closeAndRestore();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post("/staff/status", { status });
      toast.success(`Status: ${status}`);
      closeAndRestore();
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!me) return null;

  if (!me.clocked_in) {
    return (
      <button
        ref={triggerRef}
        id="clock-status-trigger"
        type="button"
        onClick={clockIn}
        disabled={busy}
        data-testid="clock-in-btn"
        className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-medium hover:bg-emerald-100 disabled:opacity-60"
        title="Mark yourself as available so new work routes to you"
      >
        <PlayCircle className="w-3.5 h-3.5" /> Clock in
      </button>
    );
  }

  const status = me.shift?.status || "available";
  const elapsed = formatElapsed(me.shift?.clock_in_at);
  const statusColor = {
    available: "border-emerald-200 bg-emerald-50 text-emerald-800",
    busy:      "border-amber-200  bg-amber-50  text-amber-800",
    on_break:  "border-slate-300  bg-white     text-slate-700",
  }[status] || "border-slate-300 bg-white text-slate-700";

  return (
    <div className="relative" data-testid="clock-widget">
      <button
        type="button"
        onClick={() => setMenu((m) => !m)}
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium ${statusColor}`}
        data-testid="clock-status-btn"
        aria-haspopup="menu"
        aria-expanded={menu}
        aria-controls="clock-status-menu"
      >
        <Clock className="w-3.5 h-3.5" />
        <span className="capitalize">{status.replace("_", " ")}</span>
        <span className="opacity-70">· {elapsed}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {menu && (
        <>
          <button type="button" tabIndex={-1} className="fixed inset-0 z-30" onClick={closeAndRestore} aria-hidden="true" data-popover-backdrop />
          <div ref={panelRef} id="clock-status-menu" role="menu" aria-labelledby="clock-status-trigger"
               className="absolute right-0 mt-1.5 w-56 rounded-lg border border-slate-200 bg-white shadow-lg z-40 py-1" data-testid="clock-menu">
            <StatusItem icon={PlayCircle} label="Available" active={status === "available"} onClick={() => setStatus("available")} testId="clock-status-available" />
            <StatusItem icon={PauseCircle} label="Busy" active={status === "busy"} onClick={() => setStatus("busy")} testId="clock-status-busy" />
            <StatusItem icon={Coffee} label="On break" active={status === "on_break"} onClick={() => setStatus("on_break")} testId="clock-status-on_break" />
            <div className="my-1 border-t border-slate-100" />
            <button
              type="button"
              role="menuitem"
              onClick={clockOut}
              disabled={busy}
              data-testid="clock-out-btn"
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-rose-50 hover:text-rose-700 inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Clock out
            </button>
            <Link
              role="menuitem"
              to="/app/staff"
              onClick={() => setMenu(false)}
              data-testid="clock-team-link"
              className="block px-3 py-2 text-xs text-slate-500 hover:bg-slate-50"
            >
              See who&apos;s online →
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

function StatusItem({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={active}
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left px-3 py-2 text-sm inline-flex items-center gap-2 ${
        active ? "bg-emerald-50 text-emerald-800 font-medium" : "text-slate-700 hover:bg-slate-50"
      }`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
      {active && <span className="ml-auto text-[10px]">•</span>}
    </button>
  );
}

function formatElapsed(isoStart) {
  if (!isoStart) return "";
  try {
    const start = new Date(isoStart);
    const diffMs = Date.now() - start.getTime();
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h${m ? ` ${m}m` : ""}`;
  } catch { return ""; }
}
